import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Briefcase, CalendarPlus, ShoppingBag, Sparkles } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { useAuth, dashboardFor } from "@/context/AuthContext";
import { TERMS_VERSION, PRIVACY_VERSION } from "@/pages/Auth";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const OPTIONS = [
  { role: "vendor", label: "Vendor", desc: "List your tutoring, classes or services", icon: Briefcase },
  { role: "event_creator", label: "Event Creator", desc: "Host events for local families", icon: CalendarPlus },
  { role: "marketplace_user", label: "Marketplace Seller", desc: "Sell books, curricula & supplies", icon: ShoppingBag },
];

export const AddRoleCard = () => {
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [held, setHeld] = useState(null);
  const [picked, setPicked] = useState(null);
  const [terms, setTerms] = useState(false);
  const [privacy, setPrivacy] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/auth/my-roles").then((r) => setHeld((r.data.roles || []).map((x) => x.role))).catch(() => setHeld([]));
  }, []);

  const available = OPTIONS.filter((o) => held && !held.includes(o.role));
  if (!available.length) return null;

  const open = (o) => { setPicked(o); setTerms(false); setPrivacy(false); };

  const confirm = async () => {
    setBusy(true);
    try {
      await api.post("/auth/add-role", { role: picked.role, terms_version: TERMS_VERSION, privacy_version: PRIVACY_VERSION });
      const fresh = await refresh();
      toast.success(`${picked.label} profile created — let's set it up`);
      setPicked(null);
      if (fresh) navigate(dashboardFor(fresh));
    } catch (e) {
      toast.error(errMsg(e, "Could not add this profile"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mb-10 rounded-2xl border border-sage/30 bg-white/70 p-5" data-testid="add-role-card">
      <p className="flex items-center gap-2 text-xs font-black uppercase tracking-wide text-sage-dark">
        <Sparkles className="w-4 h-4" /> Run a business too?
      </p>
      <p className="text-sm text-forest/70 mt-1">Add a business profile to this account — same sign-in, switch any time.</p>
      <div className="grid sm:grid-cols-3 gap-3 mt-4">
        {available.map((o) => (
          <button key={o.role} type="button" onClick={() => open(o)}
            className="flex items-center gap-3 text-left rounded-xl border border-sage/20 bg-cream/60 p-3 hover:bg-sage-light/50 transition-colors"
            data-testid={`add-role-btn-${o.role}`}>
            <span className="w-9 h-9 rounded-lg bg-sage-light flex items-center justify-center shrink-0"><o.icon className="w-4 h-4 text-sage-dark" /></span>
            <span>
              <span className="block font-heading font-bold text-forest text-sm">Become a {o.label}</span>
              <span className="block text-xs text-forest/60">{o.desc}</span>
            </span>
          </button>
        ))}
      </div>
      <Dialog open={!!picked} onOpenChange={(v) => { if (!v && !busy) setPicked(null); }}>
        <DialogContent className="rounded-3xl bg-cream" data-testid="add-role-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-forest">Add a {picked?.label} profile</DialogTitle>
            <DialogDescription className="text-forest/70">
              You'll set up your business profile next. An admin reviews it before it goes live (usually within 1-2 business days). Your Parent profile stays exactly as it is.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm text-forest">
            <label className="flex items-start gap-2">
              <Checkbox checked={terms} onCheckedChange={(v) => setTerms(!!v)} data-testid="add-role-terms-checkbox" />
              <span>I accept the <a href="/terms" target="_blank" rel="noreferrer" className="font-bold underline">Terms & Conditions</a></span>
            </label>
            <label className="flex items-start gap-2">
              <Checkbox checked={privacy} onCheckedChange={(v) => setPrivacy(!!v)} data-testid="add-role-privacy-checkbox" />
              <span>I accept the <a href="/privacy" target="_blank" rel="noreferrer" className="font-bold underline">Privacy Policy</a></span>
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPicked(null)} disabled={busy} data-testid="add-role-cancel-btn">Cancel</Button>
            <Button onClick={confirm} disabled={!terms || !privacy || busy} className="bg-sage hover:bg-sage-dark text-white" data-testid="add-role-confirm-btn">
              {busy ? "Creating…" : "Create profile"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
